import {
    Button,
    Pagination,
    Popconfirm,
    Spin,
    Table,
    Tag,
    Input,
    message,
    Switch,
    Select,
    Flex,
    Popover,
    Form,
    Drawer,
    Modal,
    Divider,
    Tooltip,
    Avatar,
} from "antd";
import React, { useEffect, useState } from "react";
import { ExtractDate, ExtractTime, dispatchtoast, messageConfiguration } from "../../../utils";
import {
    addRestro,
    getordersDetails,
    getrestroList,
    restroBrand,
    updateRestroBrand,
    restroDisabled,
    userRegister,
    updateRestroDetails,   // NEW — edit (details or password)
    deleteRestroPermanent, // NEW — permanent delete
} from "../../../ApiServices/Apifun";
import { FormDataPost } from "../../../ApiServices/apiMethod";
import {
    DeleteOutlined,
    LoadingOutlined,
    PlusCircleOutlined,
    PlusOutlined,
    UploadOutlined,
    BgColorsOutlined,
    CheckCircleFilled,
    PictureOutlined,
    EditOutlined,
    LockOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ComponentTitle from "../../../Common/ComponentTitle";
import errorImage from "../../../assets/images/food.png";
import { Card } from "antd/es";

// ---------------------------------------------------------------------------
// Static config
// ---------------------------------------------------------------------------

// Fields that make up a restaurant's theme. Keeping this as a single source
// of truth drives both the color pickers and the payload sent to the API.
const COLOR_FIELDS = [
    { key: "theme_color", label: "Theme", defaultColor: "#000000" },
    { key: "bg_color1", label: "Background 1", defaultColor: "#FFFFFF" },
    { key: "bg_color2", label: "Background 2", defaultColor: "#F5F5F5" },
    { key: "bg_color3", label: "Background 3", defaultColor: "#E8E8E8" },
    { key: "text_color", label: "Text", defaultColor: "#333333" },
];

// Ready-made theme presets so restaurant owners/admins don't have to hand
// pick five colors from scratch. "Default" mirrors the platform's fallback
// theme used when a restaurant hasn't customized anything yet.
const THEME_PRESETS = [
    {
        name: "Default",
        colors: { theme_color: "#000000", bg_color1: "#FFFFFF", bg_color2: "#F5F5F5", bg_color3: "#E8E8E8", text_color: "#333333" },
    },
    {
        name: "Purple",
        colors: { theme_color: "#6E11B0", bg_color1: "#FFFFFF", bg_color2: "#F3EBFB", bg_color3: "#E4D2F5", text_color: "#2B1140" },
    },
    {
        name: "Emerald",
        colors: { theme_color: "#0F9D58", bg_color1: "#FFFFFF", bg_color2: "#E9F8EF", bg_color3: "#C9EFDA", text_color: "#0B3D26" },
    },
    {
        name: "Sunset",
        colors: { theme_color: "#E85D28", bg_color1: "#FFFBF5", bg_color2: "#FDECDD", bg_color3: "#FAD3B4", text_color: "#4A2411" },
    },
    {
        name: "Midnight",
        colors: { theme_color: "#1E293B", bg_color1: "#F8FAFC", bg_color2: "#E2E8F0", bg_color3: "#CBD5E1", text_color: "#0F172A" },
    },
    {
        name: "Rose",
        colors: { theme_color: "#BE185D", bg_color1: "#FFF5F8", bg_color2: "#FCE4EC", bg_color3: "#F7BFD4", text_color: "#4A0F27" },
    },
];

const DEFAULT_THEME = THEME_PRESETS[0].colors;

const RestroList = () => {
    const [orderData, setOrderData] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [addLoader, setAddLoader] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [form] = Form.useForm();

    const { Search } = Input;
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const from = location.state?.from;

    // -----------------------------------------------------------------
    // Branding modal state — a single modal is reused for every row,
    // keyed off `brandingRecord`. This avoids mounting one modal per
    // table row and keeps the upload/color logic in one place.
    // -----------------------------------------------------------------
    const [brandingModalOpen, setBrandingModalOpen] = useState(false);
    const [brandingRecord, setBrandingRecord] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [themeDraft, setThemeDraft] = useState(DEFAULT_THEME);
    const [brandingSaving, setBrandingSaving] = useState(false);

    // -----------------------------------------------------------------
    // Edit modal state
    // -----------------------------------------------------------------
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [editForm] = Form.useForm();
    const [editSaving, setEditSaving] = useState(false);

    // -----------------------------------------------------------------
    // Change password modal state (separate flow — same PUT endpoint,
    // payload contains only `password`)
    // -----------------------------------------------------------------
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [passwordRecord, setPasswordRecord] = useState(null);
    const [passwordForm] = Form.useForm();
    const [passwordSaving, setPasswordSaving] = useState(false);

    // -----------------------------------------------------------------
    // Delete state
    // -----------------------------------------------------------------
    const [deletingId, setDeletingId] = useState(null);

    // -----------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------
    const FetchordersDetails = (page) => {
        setIsLoading(true);
        getrestroList(`?page=${page}&limit=5&search=${searchTerm}`)
            .then((response) => {
                if (response.status) {
                    setOrderData(response.data || []);
                    setCurrentPage(response.pagination.currentPage || 1);
                    setTotalPages(response.pagination.totalPages || 0);
                }
                setIsLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching order details:", err);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        FetchordersDetails(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm]);

    useEffect(() => {
        return () => {
            if (previewImage) URL.revokeObjectURL(previewImage);
        };
    }, [previewImage]);

    const handlePageChange = (page) => setCurrentPage(page);

    const handleErrorImage = (e) => {
        e.target.src = errorImage;
    };

    // -----------------------------------------------------------------
    // Restaurant status toggle
    // -----------------------------------------------------------------
    const handleChangeStatus = (rowId, status) => {
        setIsLoading(true);
        const newStatus = status == 1 ? 0 : 1;

        restroDisabled(`?restaurantId=${rowId}&isActive=${newStatus}`)
            .then((response) => {
                if (response.status) {
                    message.success("Status updated successfully");
                    FetchordersDetails(currentPage);
                } else {
                    message.error("Failed to update status");
                }
                setIsLoading(false);
            })
            .catch((err) => {
                console.error("Error updating status:", err);
                message.error("An error occurred while updating the status");
                setIsLoading(false);
            });
    };

    const popoverContent = (rowId, status) => (
        <div className="w-52">
            <p className="mb-2 text-sm">
                {status == 1
                    ? "Deactivate this restaurant?"
                    : "Activate this restaurant?"}
            </p>
            <Button
                type="primary"
                size="small"
                block
                onClick={() => handleChangeStatus(rowId, status)}
                loading={isLoading}
            >
                Confirm
            </Button>
        </div>
    );

    // -----------------------------------------------------------------
    // Add restaurant (drawer form)
    // -----------------------------------------------------------------
    const handleFinish = (val) => {
        setAddLoader(true);
        addRestro({
            mobile_number: val.mobile_number,
            email: val.email,
            name: val.name,
            address: val.address,
            password: val.password,
            commissionAmount: val.commissionAmount,
        })
            .then((res) => {
                if (res.status) {
                    dispatchtoast(res.message);
                    setAddLoader(false);
                    setOpenModal(false);
                    form.resetFields();
                    FetchordersDetails(currentPage);
                } else {
                    setAddLoader(false);
                }
            })
            .catch((err) => {
                console.error(err);
                setAddLoader(false);
            });
    };

    const handleDrawerClose = () => setOpenModal(false);

    // -----------------------------------------------------------------
    // Edit restaurant details
    // -----------------------------------------------------------------
    const openEditModal = (record) => {
        setEditingRecord(record);
        editForm.setFieldsValue({
            name: record.name,
            email: record.email,
            mobile_number: record.mobile_number,
            address: record.address,
            commissionAmount: record.commissionAmount,
            is_active: record.is_active == 1,
        });
        setEditModalOpen(true);
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setEditingRecord(null);
        editForm.resetFields();
    };

    const handleEditFinish = (values) => {
        if (!editingRecord) return;
        setEditSaving(true);

        updateRestroDetails(editingRecord.id, {
            name: values.name,
            mobile_number: values.mobile_number,
            email: values.email,
            address: values.address,
            commissionAmount: values.commissionAmount,
            is_active: values.is_active ? "1" : "0",
        })
            .then((res) => {
                if (res && res.status) {
                    message.success(res.message || "Restaurant updated successfully");
                    closeEditModal();
                    FetchordersDetails(currentPage);
                } else {
                    message.error((res && res.message) || "Failed to update restaurant");
                }
                setEditSaving(false);
            })
            .catch((err) => {
                console.error(err);
                message.error("An error occurred while updating the restaurant");
                setEditSaving(false);
            });
    };

    // -----------------------------------------------------------------
    // Change password (separate modal, same PUT endpoint — payload only
    // contains `password`)
    // -----------------------------------------------------------------
    const openPasswordModal = (record) => {
        setPasswordRecord(record);
        passwordForm.resetFields();
        setPasswordModalOpen(true);
    };

    const closePasswordModal = () => {
        setPasswordModalOpen(false);
        setPasswordRecord(null);
        passwordForm.resetFields();
    };

    const handlePasswordFinish = (values) => {
        if (!passwordRecord) return;
        setPasswordSaving(true);

        updateRestroDetails(passwordRecord.id, { password: values.password })
            .then((res) => {
                if (res && res.status) {
                    message.success(res.message || "Password updated successfully");
                    closePasswordModal();
                } else {
                    message.error((res && res.message) || "Failed to update password");
                }
                setPasswordSaving(false);
            })
            .catch((err) => {
                console.error(err);
                message.error("An error occurred while updating the password");
                setPasswordSaving(false);
            });
    };

    // -----------------------------------------------------------------
    // Delete restaurant (permanent — irreversible, confirmed via Popconfirm)
    // -----------------------------------------------------------------
    const handleDelete = (record) => {
        setDeletingId(record.id);
        deleteRestroPermanent(record.id)
            .then((res) => {
                if (res && res.status) {
                    message.success(res.message || "Restaurant deleted permanently");
                    FetchordersDetails(currentPage);
                } else {
                    message.error((res && res.message) || "Failed to delete restaurant");
                }
                setDeletingId(null);
            })
            .catch((err) => {
                console.error(err);
                message.error("An error occurred while deleting the restaurant");
                setDeletingId(null);
            });
    };

    // -----------------------------------------------------------------
    // Branding helpers
    // -----------------------------------------------------------------
    const getBrandingLogoUrl = (record) => {
        if (!record) return null;
        if (record.imagePath) return record.imagePath;
        if (!record.branding) return null;

        const branding = record.branding;
        if (typeof branding === "string") return branding;

        const candidates = [
            "logo_image", "logo", "image", "logoUrl", "logoURL",
            "imageUrl", "imageURL", "photo", "icon",
        ];

        for (const key of candidates) {
            const value = branding[key];
            if (!value) continue;
            if (typeof value === "string" && value.trim()) return value;
            if (typeof value === "object" && typeof value.url === "string" && value.url.trim()) return value.url;
        }
        return null;
    };

    const getExistingTheme = (record) => {
        const branding = record?.branding;
        if (!branding || typeof branding !== "object") return { ...DEFAULT_THEME };
        const theme = { ...DEFAULT_THEME };
        COLOR_FIELDS.forEach(({ key }) => {
            if (branding[key]) theme[key] = branding[key];
        });
        return theme;
    };

    // Opens the branding modal for a given row, pre-filling its current logo
    // preview and theme colors (or the platform default if none is set yet).
    const openBrandingModal = (record) => {
        setBrandingRecord(record);
        setSelectedFile(null);
        setPreviewImage(null);
        setThemeDraft(getExistingTheme(record));
        setBrandingModalOpen(true);
    };

    const closeBrandingModal = () => {
        if (previewImage) URL.revokeObjectURL(previewImage);
        setBrandingModalOpen(false);
        setBrandingRecord(null);
        setSelectedFile(null);
        setPreviewImage(null);
    };

    const handleFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        if (previewImage) URL.revokeObjectURL(previewImage);
        setSelectedFile(file);
        setPreviewImage(URL.createObjectURL(file));
    };

    const handleColorChange = (colorKey, value) => {
        setThemeDraft((prev) => ({ ...prev, [colorKey]: value }));
    };

    const applyPreset = (preset) => {
        setThemeDraft({ ...preset.colors });
    };

    const isPresetActive = (preset) =>
        COLOR_FIELDS.every(({ key }) => themeDraft[key]?.toLowerCase() === preset.colors[key].toLowerCase());

    const handleSaveBranding = () => {
        if (!brandingRecord) return;
        const rowId = brandingRecord.id;
        const existingLogo = getBrandingLogoUrl(brandingRecord);
        const useUpdate = !!existingLogo;

        setBrandingSaving(true);
        const formData = new FormData();
        if (selectedFile) formData.append("logo_image", selectedFile);
        formData.append("restaurantId", rowId);
        COLOR_FIELDS.forEach(({ key, defaultColor }) => {
            formData.append(key, themeDraft[key] || defaultColor);
        });
        formData.append("title", "logo");

        const apiCall = useUpdate ? updateRestroBrand : restroBrand;
        const param = useUpdate ? `${rowId}` : "";

        apiCall(formData, param)
            .then((res) => {
                if (res && res.status) {
                    message.success(res.message || "Branding updated successfully");
                    closeBrandingModal();
                    FetchordersDetails(currentPage);
                } else {
                    message.error((res && res.message) || "Failed to update branding");
                }
                setBrandingSaving(false);
            })
            .catch((err) => {
                console.error(err);
                message.error("An error occurred while updating the branding");
                setBrandingSaving(false);
            });
    };

    // -----------------------------------------------------------------
    // Table columns
    // -----------------------------------------------------------------
    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            className: "custom-header",
            render: (text) => <span className="font-medium text-slate-800">{text}</span>,
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            className: "custom-header whitespace-nowrap",
            render: (text) => <span className="text-slate-600">{text || "-"}</span>,
        },
        {
            title: "Phone no.",
            dataIndex: "mobile_number",
            key: "mobile_number",
            className: "custom-header",
            render: (text) => <span className="text-slate-600">{text || "-"}</span>,
        },
        {
            title: "Created Time",
            dataIndex: "register_date",
            key: "register_date",
            className: "custom-header",
            render: (_, record) => (
                <p className="text-xs text-slate-500">
                    {ExtractDate(record.createdAt)} · {ExtractTime(record.createdAt)}
                </p>
            ),
        },
        {
            title: "Status",
            dataIndex: "is_active",
            key: "is_active",
            className: "custom-header",
            render: (_, record) => (
                <Popover
                    content={popoverContent(record.id, record.is_active)}
                    trigger="click"
                    placement="bottom"
                >
                    <Tag
                        style={{
                            fontWeight: 600,
                            color: record.is_active == 1 ? "#0F9D58" : "#D9363E",
                            backgroundColor: record.is_active == 1 ? "#E9F8EF" : "#FFECEC",
                            borderColor: record.is_active == 1 ? "#B7E4C7" : "#FFCACA",
                            borderRadius: "4px",
                            cursor: "pointer",
                        }}
                    >
                        {record.is_active == 1 ? "Active" : "Inactive"}
                    </Tag>
                </Popover>
            ),
        },
        {
            title: "Branding",
            dataIndex: "branding",
            key: "branding",
            className: "custom-header",
            render: (_, record) => {
                const logoUrl = getBrandingLogoUrl(record);
                const theme = getExistingTheme(record);

                return (
                    <div className="flex items-center gap-3">
                        {logoUrl ? (
                            <img
                                src={logoUrl}
                                alt="Restaurant logo"
                                onError={handleErrorImage}
                                className="h-10 w-10 rounded-md object-cover border border-slate-200"
                            />
                        ) : (
                            <Avatar
                                shape="square"
                                size={40}
                                icon={<PictureOutlined />}
                                className="bg-slate-100 text-slate-400"
                            />
                        )}

                        {/* Small theme swatch strip preview */}
                        <div className="flex gap-1">
                            {COLOR_FIELDS.slice(0, 3).map(({ key }) => (
                                <span
                                    key={key}
                                    className="h-4 w-4 rounded-full border border-slate-200"
                                    style={{ backgroundColor: theme[key] }}
                                />
                            ))}
                        </div>

                        <Tooltip title="Manage logo & theme">
                            <Button
                                size="small"
                                icon={<BgColorsOutlined />}
                                onClick={() => openBrandingModal(record)}
                            >
                                Manage
                            </Button>
                        </Tooltip>
                    </div>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            className: "custom-header",
            render: (_, record) => (
                <div className="flex items-center gap-2">
                    <Tooltip title="Edit details">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Change password">
                        <Button
                            size="small"
                            icon={<LockOutlined />}
                            onClick={() => openPasswordModal(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete this restaurant?"
                        description="This permanently deletes the restaurant and cannot be undone."
                        okText="Delete"
                        okButtonProps={{ danger: true, loading: deletingId === record.id }}
                        onConfirm={() => handleDelete(record)}
                    >
                        <Tooltip title="Delete permanently">
                            <Button size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    return (
        <div className="p-4">
            {/* Search */}
            <div className="flex justify-end mb-4">
                <Input
                    placeholder="Search restaurants..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: "26%" }}
                    allowClear
                />
            </div>

            <Card
                title="Restaurant Data"
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setOpenModal(true)}
                        className="font-semibold"
                    >
                        Add Restro
                    </Button>
                }
                className="shadow-sm shadow-gray-300"
            >
                <Table
                    bordered
                    loading={isLoading}
                    columns={columns}
                    dataSource={orderData}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    className="history-table animate-fade-in max-h-[500px] overflow-x-auto rounded-lg"
                />
            </Card>

            {/* Pagination */}
            <Pagination
                className="mt-4 text-xs w-full flex justify-end"
                total={totalPages * 10}
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${totalPages * 10} items`}
                onChange={handlePageChange}
                defaultPageSize={10}
                current={currentPage}
            />

            {/* -------------------------------------------------------- */}
            {/* Add Restro Drawer                                       */}
            {/* -------------------------------------------------------- */}
            <Drawer
                title="Add Restro"
                width={400}
                onClose={handleDrawerClose}
                open={openModal}
                bodyStyle={{ paddingBottom: 80 }}
            >
                <Form layout="vertical" form={form} onFinish={handleFinish}>
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: "Please enter Restro name" }]}
                    >
                        <Input placeholder="Enter Restro name" />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: "Please enter your email" },
                            { type: "email", message: "Please enter a valid email" },
                        ]}
                    >
                        <Input placeholder="Enter your email" />
                    </Form.Item>
                    <Form.Item
                        name="mobile_number"
                        label="Mobile Number"
                        rules={[
                            { required: true, message: "Please enter your mobile number" },
                            { pattern: /^[6-9]\d{9}$/, message: "Mobile number is invalid" },
                        ]}
                    >
                        <Input placeholder="Enter your mobile number" />
                    </Form.Item>
                    <Form.Item
                        name="commissionAmount"
                        label="Commission Amount"
                        rules={[
                            { required: true, message: "Please enter your commission Amount" },
                        ]}
                    >
                        <Input placeholder="Enter your commission Amount in %" />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        label="Password"
                        rules={[{ required: true, message: "Please enter your Password" }]}
                    >
                        <Input placeholder="Enter your Password" />
                    </Form.Item>
                    <Form.Item
                        name="address"
                        label="Address"
                        rules={[{ required: true, message: "Please enter your Address" }]}
                    >
                        <Input placeholder="Enter your Address" />
                    </Form.Item>

                    <Button loading={addLoader} type="primary" htmlType="submit" block>
                        Add Restro
                    </Button>
                </Form>
            </Drawer>

            {/* -------------------------------------------------------- */}
            {/* Edit Restro Modal                                       */}
            {/* -------------------------------------------------------- */}
            <Modal
                title={editingRecord ? `Edit · ${editingRecord.name}` : "Edit Restaurant"}
                open={editModalOpen}
                onCancel={closeEditModal}
                footer={null}
                destroyOnClose
            >
                <Form layout="vertical" form={editForm} onFinish={handleEditFinish}>
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: "Please enter Restro name" }]}
                    >
                        <Input placeholder="Enter Restro name" />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: "Please enter email" },
                            { type: "email", message: "Please enter a valid email" },
                        ]}
                    >
                        <Input placeholder="Enter email" />
                    </Form.Item>
                    <Form.Item
                        name="mobile_number"
                        label="Mobile Number"
                        rules={[
                            { required: true, message: "Please enter mobile number" },
                            { pattern: /^[6-9]\d{9}$/, message: "Mobile number is invalid" },
                        ]}
                    >
                        <Input placeholder="Enter mobile number" />
                    </Form.Item>
                    <Form.Item
                        name="commissionAmount"
                        label="Commission Amount"
                        rules={[
                            { required: true, message: "Please enter commission amount" },
                        ]}
                    >
                        <Input placeholder="Enter commission amount in %" />
                    </Form.Item>
                    <Form.Item
                        name="address"
                        label="Address"
                        rules={[{ required: true, message: "Please enter address" }]}
                    >
                        <Input placeholder="Enter address" />
                    </Form.Item>
                    <Form.Item name="is_active" label="Active" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <div className="flex gap-2 justify-end">
                        <Button onClick={closeEditModal}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={editSaving}>
                            Save changes
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* -------------------------------------------------------- */}
            {/* Change Password Modal                                   */}
            {/* -------------------------------------------------------- */}
            <Modal
                title={passwordRecord ? `Change Password · ${passwordRecord.name}` : "Change Password"}
                open={passwordModalOpen}
                onCancel={closePasswordModal}
                footer={null}
                destroyOnClose
            >
                <Form layout="vertical" form={passwordForm} onFinish={handlePasswordFinish}>
                    <Form.Item
                        name="password"
                        label="New Password"
                        rules={[
                            { required: true, message: "Please enter a new password" },
                            { min: 8, message: "Password must be at least 8 characters" },
                        ]}
                    >
                        <Input.Password placeholder="Enter new password" />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label="Confirm Password"
                        dependencies={["password"]}
                        rules={[
                            { required: true, message: "Please confirm the new password" },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue("password") === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error("Passwords do not match"));
                                },
                            }),
                        ]}
                    >
                        <Input.Password placeholder="Re-enter new password" />
                    </Form.Item>

                    <div className="flex gap-2 justify-end">
                        <Button onClick={closePasswordModal}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={passwordSaving}>
                            Update password
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* -------------------------------------------------------- */}
            {/* Branding Modal — logo upload + theme color popup        */}
            {/* -------------------------------------------------------- */}
            <Modal
                title={brandingRecord ? `Branding · ${brandingRecord.name}` : "Branding"}
                open={brandingModalOpen}
                onCancel={closeBrandingModal}
                width={560}
                footer={[
                    <Button key="cancel" onClick={closeBrandingModal}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        loading={brandingSaving}
                        onClick={handleSaveBranding}
                    >
                        Save changes
                    </Button>,
                ]}
                destroyOnClose
            >
                {/* Logo upload */}
                <div>
                    <p className="text-sm font-semibold text-slate-700 mb-2">Logo</p>
                    <div className="flex items-center gap-4">
                        <div className="h-16 w-16 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden">
                            {previewImage || getBrandingLogoUrl(brandingRecord) ? (
                                <img
                                    src={previewImage || getBrandingLogoUrl(brandingRecord)}
                                    alt="Logo"
                                    onError={handleErrorImage}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <PictureOutlined className="text-2xl text-slate-300" />
                            )}
                        </div>
                        <div>
                            <Button icon={<UploadOutlined />}>
                                <label className="cursor-pointer">
                                    Choose file
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                </label>
                            </Button>
                            {selectedFile && (
                                <p className="text-xs text-slate-500 mt-1">{selectedFile.name}</p>
                            )}
                        </div>
                    </div>
                </div>

                <Divider />

                {/* Theme presets */}
                <div>
                    <p className="text-sm font-semibold text-slate-700 mb-2">Theme presets</p>
                    <div className="grid grid-cols-3 gap-2">
                        {THEME_PRESETS.map((preset) => {
                            const active = isPresetActive(preset);
                            return (
                                <button
                                    type="button"
                                    key={preset.name}
                                    onClick={() => applyPreset(preset)}
                                    className={`relative flex items-center gap-2 rounded-md border px-2 py-2 text-left transition ${
                                        active ? "border-purple-500 ring-1 ring-purple-400" : "border-slate-200 hover:border-slate-300"
                                    }`}
                                >
                                    <span className="flex -space-x-1">
                                        {COLOR_FIELDS.slice(0, 3).map(({ key }) => (
                                            <span
                                                key={key}
                                                className="h-5 w-5 rounded-full border border-white"
                                                style={{ backgroundColor: preset.colors[key] }}
                                            />
                                        ))}
                                    </span>
                                    <span className="text-xs font-medium text-slate-700">{preset.name}</span>
                                    {active && (
                                        <CheckCircleFilled className="absolute right-1.5 top-1.5 text-purple-500 text-xs" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <Divider />

                {/* Custom colors */}
                <div>
                    <p className="text-sm font-semibold text-slate-700 mb-2">Custom colors</p>
                    <div className="grid grid-cols-5 gap-2">
                        {COLOR_FIELDS.map((field) => (
                            <div key={field.key} className="flex flex-col items-center gap-1">
                                <label className="text-xs text-slate-600 text-center">{field.label}</label>
                                <input
                                    type="color"
                                    value={themeDraft[field.key] || field.defaultColor}
                                    onChange={(e) => handleColorChange(field.key, e.target.value)}
                                    className="w-9 h-9 rounded cursor-pointer border border-slate-300"
                                    title={field.label}
                                />
                                <span className="text-[10px] text-slate-500 font-mono">
                                    {themeDraft[field.key] || field.defaultColor}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default RestroList;